/**
 * Dashboard templates — the Templates tab and the Use-template flow.
 *
 * Contract source: `dashboard_module_frontend_contract.md` §11–§12.
 */

import { validateApiResponse, normalizePagedMeta } from '../shared/utils/apiResponse.js';
import { apiDashboards, DASHBOARD_API_PREFIX } from './apiClient.js';
import { mapApiWidgetInstanceToUi } from './widgetInstances.js';
import { toUiState } from './dashboards.js';

const DEFAULT_PAGE_SIZE = 20;

/** `origin` selects who published the template. */
export const TEMPLATE_ORIGINS = Object.freeze(['PLATFORM', 'TENANT', 'ALL']);

/** Template row → the card the Templates tab renders. */
export const mapApiTemplateToUi = (raw = {}) => ({
  id: raw.templateUuid ?? '',
  origin: raw.templateOrigin ?? '',
  templateKey: raw.templateKey ?? '',
  title: raw.name ?? '',
  description: raw.description ?? '',
  icon: raw.icon ?? null,
  sectionId: raw.sectionKey ?? '',
  sectionPath: raw.sectionName ?? raw.sectionKey ?? '',
  state: toUiState(raw.versionState) || 'published',
  // The card prints category names as its tag row, and filters by the key.
  tags: (raw.categories ?? []).map((category) => category.name ?? category.categoryKey ?? ''),
  categoryId: raw.categories?.[0]?.categoryKey ?? '',
  categoryKeys: (raw.categories ?? []).map((category) => category.categoryKey ?? ''),
  widgetCount: raw.widgetCount ?? 0,
  publishedVersionNumber: raw.publishedVersionNumber ?? null,
  publishedAt: raw.publishedAt ?? null,
  updatedAt: raw.updatedAt ?? null,
  creatorName: raw.author ?? '',
  // No preview asset in the contract; the card already falls back to its
  // generated placeholder when this is null.
  previewImage: null,
});

/** Template detail: composition for the preview, plus install requirements. */
export const mapApiTemplateDetailToUi = (raw = {}) => ({
  ...mapApiTemplateToUi(raw),
  widgets: (raw.widgets ?? []).map(mapApiWidgetInstanceToUi),
  requirements: {
    requiredWidgetDefinitions: raw.requirements?.requiredWidgetDefinitions ?? [],
    requiredCapabilities: raw.requirements?.requiredCapabilities ?? [],
    minAppVersion: raw.requirements?.minAppVersion ?? null,
  },
});

/**
 * List templates.
 *
 * The Section filter is shared with the Created tab, whose values are
 * `sectionKey`s, but `template/list` filters by `sectionTypes` — the coarser
 * dimension. The type is therefore derived from the key's prefix
 * (`CONTACT.OVERVIEW` → `CONTACT`), which is the only mapping the contract
 * allows. TODO(CT-5183 / backend): accept `sectionKeys` here so the two tabs
 * filter at the same granularity.
 */
export async function listTemplates({
  search,
  sectionId,
  categoryId,
  categoryKeys,
  origin,
  page,
  pageSize,
  orderBy,
  orderDirection,
} = {}) {
  const sectionTypes = sectionId ? [String(sectionId).split('.')[0]] : undefined;
  const keys = categoryKeys ?? (categoryId ? [categoryId] : undefined);

  const response = await apiDashboards.post(`${DASHBOARD_API_PREFIX}/template/list`, {
    ...(search ? { searchKeyword: search } : {}),
    ...(keys?.length ? { categoryKeys: keys } : {}),
    ...(sectionTypes ? { sectionTypes } : {}),
    origin: origin ?? 'ALL',
    page: page ?? 1,
    pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
    orderBy: orderBy ?? 'published_at',
    orderDirection: (orderDirection ?? 'desc').toUpperCase(),
  });

  const { body, meta } = validateApiResponse(response, 'Could not load templates.');
  return {
    templates: (body ?? []).map(mapApiTemplateToUi),
    meta: normalizePagedMeta(meta),
  };
}

/** Template detail, for preview and for checking install requirements. */
export async function getTemplate(templateId) {
  const response = await apiDashboards.get(
    `${DASHBOARD_API_PREFIX}/template/${encodeURIComponent(templateId)}`,
  );
  const { body } = validateApiResponse(response, 'Could not load the template.');
  return mapApiTemplateDetailToUi(body ?? {});
}
